/**
 * SEO Component
 *
 * A reusable component for managing all SEO-related meta tags using react-helmet-async.
 *
 * Features:
 * - Dynamic title, description, and keywords
 * - Canonical URLs for duplicate content prevention
 * - Open Graph tags for social media sharing
 * - Twitter Card tags for Twitter optimization
 * - Robots directives for search engine crawling control
 * - Structured data support (JSON-LD)
 * - Language alternatives (hreflang)
 *
 * Usage Example:
 * <SEO
 *   title="Auto Insurance Quotes | Kaphy Insurance Agency"
 *   description="Get affordable auto insurance quotes in Minneapolis..."
 *   keywords="auto insurance, car insurance, Minneapolis"
 *   canonical="/services/auto-insurance"
 *   ogType="article"
 *   robots="index, follow"
 *   structuredData={yourStructuredDataObject}
 * />
 */

import React from 'react';
import { Helmet } from 'react-helmet-async';
import { seoConfig, getFullUrl, sanitizeSeoText } from './seoConfig';

const SEO = ({
  // Basic SEO
  title,
  description,
  keywords,
  canonical,
  robots,

  // Open Graph
  ogTitle,
  ogDescription,
  ogImage,
  ogImageWidth,
  ogImageHeight,
  ogType,
  ogUrl,

  // Twitter
  twitterTitle,
  twitterDescription,
  twitterImage,
  twitterCard,

  // Advanced
  noindex = false,
  nofollow = false,
  structuredData,
  lang = 'en',
  alternateLanguages,

  // Article specific (for blog posts)
  articlePublishedTime,
  articleModifiedTime,
  articleAuthor,
  articleSection,
  articleTags,

  // Additional meta tags
  additionalMetaTags = [],
  additionalLinkTags = [],
}) => {
  // Construct final meta values with fallbacks
  const finalTitle = title || seoConfig.defaultTitle;
  const finalDescription = sanitizeSeoText(
    description || seoConfig.defaultDescription,
    160
  );
  const finalKeywords = keywords || seoConfig.defaultKeywords;
  const canonicalUrl = canonical ? getFullUrl(canonical) : null;

  // Open Graph defaults
  const finalOgTitle = ogTitle || finalTitle;
  const finalOgDescription = ogDescription || finalDescription;
  const finalOgImage = ogImage || seoConfig.defaultOgImage;
  const finalOgType = ogType || seoConfig.defaultOgType;
  const finalOgUrl = ogUrl || canonicalUrl || seoConfig.siteUrl;

  // Twitter defaults
  const finalTwitterTitle = twitterTitle || finalOgTitle;
  const finalTwitterDescription = twitterDescription || finalOgDescription;
  const finalTwitterImage = twitterImage || finalOgImage;
  const finalTwitterCard = twitterCard || seoConfig.twitterCardType;

  // Robots directive
  let finalRobots = robots || seoConfig.defaultRobots;
  if (noindex || nofollow) {
    const robotsArray = [];
    robotsArray.push(noindex ? 'noindex' : 'index');
    robotsArray.push(nofollow ? 'nofollow' : 'follow');
    finalRobots = robotsArray.join(', ');
  }

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <html lang={lang} />
      <title>{finalTitle}</title>
      <meta name="title" content={finalTitle} />
      <meta name="description" content={finalDescription} />
      {finalKeywords && <meta name="keywords" content={finalKeywords} />}
      <meta name="robots" content={finalRobots} />

      {/* Canonical URL - Critical for SEO */}
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={finalOgType} />
      <meta property="og:url" content={finalOgUrl} />
      <meta property="og:title" content={finalOgTitle} />
      <meta property="og:description" content={finalOgDescription} />
      <meta property="og:image" content={finalOgImage} />
      <meta property="og:image:width" content={ogImageWidth || seoConfig.defaultOgImageWidth} />
      <meta property="og:image:height" content={ogImageHeight || seoConfig.defaultOgImageHeight} />
      <meta property="og:site_name" content={seoConfig.siteName} />
      <meta property="og:locale" content={seoConfig.locale} />

      {/* Article specific Open Graph tags */}
      {finalOgType === 'article' && articlePublishedTime && (
        <meta property="article:published_time" content={articlePublishedTime} />
      )}
      {finalOgType === 'article' && articleModifiedTime && (
        <meta property="article:modified_time" content={articleModifiedTime} />
      )}
      {finalOgType === 'article' && articleAuthor && (
        <meta property="article:author" content={articleAuthor} />
      )}
      {finalOgType === 'article' && articleSection && (
        <meta property="article:section" content={articleSection} />
      )}
      {finalOgType === 'article' && articleTags && articleTags.map((tag, index) => (
        <meta key={index} property="article:tag" content={tag} />
      ))}

      {/* Twitter Card */}
      <meta name="twitter:card" content={finalTwitterCard} />
      <meta name="twitter:url" content={finalOgUrl} />
      <meta name="twitter:title" content={finalTwitterTitle} />
      <meta name="twitter:description" content={finalTwitterDescription} />
      <meta name="twitter:image" content={finalTwitterImage} />
      {seoConfig.twitterHandle && (
        <meta name="twitter:site" content={seoConfig.twitterHandle} />
      )}

      {/* Language Alternatives (hreflang) */}
      {alternateLanguages && Object.entries(alternateLanguages).map(([lang, url]) => (
        <link key={lang} rel="alternate" hrefLang={lang} href={url} />
      ))}

      {/* Structured Data (JSON-LD) */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}

      {/* Additional Meta Tags */}
      {additionalMetaTags.map((tag, index) => (
        <meta key={index} {...tag} />
      ))}

      {/* Additional Link Tags */}
      {additionalLinkTags.map((tag, index) => (
        <link key={index} {...tag} />
      ))}
    </Helmet>
  );
};

export default SEO;
